import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from "@angular/router";
import { Location } from "@angular/common";
import { Observable, of, concatMap } from "rxjs";
import { Inject, Injectable } from "@angular/core";
import { NeoLogService } from '@app/services/neo-log.service';
import { NavigationService } from "@app/services/navigation.service";
import { AppStateService } from "@app/services/app-state.service";

@Injectable({
  providedIn: 'root',
})
export class NeoAuthGuard extends MsalGuard {

  constructor(
    @Inject(MSAL_GUARD_CONFIG) msalGuardConfig: MsalGuardConfiguration,
    msalBroadcastService: MsalBroadcastService,
    authService: MsalService,
    location: Location,
    router: Router,
    private appStateService: AppStateService,
    private neoLogService: NeoLogService,
    private navigationService: NavigationService
  ) {
    super(msalGuardConfig, msalBroadcastService, authService, location, router);
    this.neoLogService.log('NeoAuthGuard->constructor');
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.neoLogService.log('NeoAuthGuard->canActivate START', { url: state.url });

    // Bypass guard for form submission in kiosk mode
    if (NavigationService.isFormSubmissionInKioskMode(state.url)) {
      this.neoLogService.log('NeoAuthGuard->canActivate', 'Bypassing guard for formsubmission in kiosk mode');
      return of(true);
    }

    // Check if app is fully initialized (authentication + profile + feature flags)
    if (!this.appStateService.isReady()) {
      this.neoLogService.log('NeoAuthGuard->canActivate', 'App not ready, redirecting to /auth-check');
      const authCheckUrl = this.navigationService.generateAuthCheckUrlTree(state.url);
      return of(authCheckUrl);
    }

    // App is ready, now let MsalGuard verify the MSAL token is still valid
    this.neoLogService.log('NeoAuthGuard->canActivate', 'App ready, calling super.canActivate (MsalGuard)');
    const result = super.canActivate(route, state);

    // Log what MsalGuard returns for debugging
    if (result instanceof Observable) {
      return result.pipe(
        concatMap((guardResult) => {
          this.neoLogService.log('NeoAuthGuard->canActivate->MsalGuard returned', guardResult);

          // If MsalGuard blocks (user logged out), redirect to auth-check
          if (guardResult === false || guardResult instanceof UrlTree) {
            this.neoLogService.log('NeoAuthGuard->canActivate->MsalGuard blocked, redirecting to /auth-check');
            const authCheckUrl = this.navigationService.generateAuthCheckUrlTree(state.url);
            return of(authCheckUrl);
          }

          return of(guardResult);
        })
      );
    }

    this.neoLogService.log('NeoAuthGuard->canActivate->MsalGuard returned (non-observable)', result);
    return of(result as boolean | UrlTree);
  }

  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.neoLogService.log('NeoAuthGuard->canActivateChild START', { url: state.url });

    // Use same logic as canActivate
    return this.canActivate(route, state);
  }

}

